import { FC, useMemo } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import Modal from '@srm/ui/modal';
import Button from '@srm/ui/button';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';
import { IDocumentTable, ISplitRows } from '@models/Forms/IForms';
import { IFieldElem } from '@models/IFormData';
import EditTable from './EditTable';
import { TableRow } from './tableColumns';

const SPLIT_FIELDS: Record<string, IFieldElem> = {
    splitRows: { name: 'fields.[0].value', index: 0 } as IFieldElem,
};

/** «Тиражирование»: варианты вводятся в локальную мини-таблицу (собственная
 *  RHF-форма + EditTable по схеме settings.columns) — замена SplitRowsForm
 *  без DX; каждая выбранная строка развернётся по вариантам. */
const SplitRowsModal: FC<{
    table: IDocumentTable;
    settings: ISplitRows;
    onApply: (variants: TableRow[]) => void;
    onClose: () => void;
}> = ({ table, settings, onApply, onClose }) => {
    const formMethods = useForm({ defaultValues: { fields: [{ value: [] }] } as never });
    const hasUnsavedInput = useUnsavedFormInput(formMethods);

    const splitTable = useMemo<IDocumentTable>(
        () =>
            ({
                key: 'splitRows',
                id: `${table.id ?? table.key}-split`,
                tableColumn: settings.columns?.tableColumn ?? [],
            }) as IDocumentTable,
        [table, settings],
    );

    const onApplyClick = () => {
        const value = formMethods.getValues('fields.[0].value' as never) as unknown;
        const variants = Array.isArray(value) ? (value as TableRow[]) : [];
        if (variants.length > 0) onApply(variants);
        onClose();
    };

    return (
        <Modal
            size="m"
            onClose={onClose}
            hasUnsavedInput={hasUnsavedInput}
            header="Тиражирование"
            footer={
                <div className="modal-dlg-buttons">
                    <Button variant="primary" size="sm" aria-label="Применить тиражирование" onClick={onApplyClick}>
                        Применить
                    </Button>
                    <Button variant="light" size="sm" aria-label="Отменить тиражирование" onClick={onClose}>
                        Отменить
                    </Button>
                </div>
            }
        >
            <EditTable
                table={splitTable}
                formMethods={formMethods as UseFormReturn<never>}
                fields={SPLIT_FIELDS}
                isEdit
                isNew={false}
            />
        </Modal>
    );
};

export default SplitRowsModal;
